import {
	CircleGeometry,
	LinearSRGBColorSpace,
	Mesh,
	MeshStandardMaterial,
	RepeatWrapping,
	Shape,
	ShapeGeometry,
	SRGBColorSpace,
	Vector2,
} from 'three';
import { ProjectRenderer } from '../ProjectRenderer';
import { PlaygroundManager } from '../managers/PlaygroundManager';
import { RenderUtils } from '../utils/RenderUtils';
import { ThreeEvent } from '../types';
import { Asset, MapType } from '@/api/AssetApi';
import { getFileSrc } from '@/utils/utilities';
import { AssetsLib } from '../utils/AssetsLib';
import { Loader } from '../utils/Loader';
import { PlaygroundShapeType } from '@/types/creator';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LINE_POS_Y, lineMaterial } from './PlaygroundLine';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { clamp, debounce } from 'lodash';
import RENDERER_CONFIG from '@/configs/rendererConfig';

export type SerializedPlaygroundFloor = {
	asset: Asset | null;
	radius: number;
};

const DEFAULT_MATERIAL = new MeshStandardMaterial({
	color: 0xffffff,
	toneMapped: false,
});

export class PlaygroundFloor {
	private loader = Loader.getInstance();
	private assetsLib = AssetsLib.getInstance();
	private customMaterial: MeshStandardMaterial | null = null;
	private asset: Asset | null = null;
	private _radius: number = RENDERER_CONFIG.CIRCLE_SHAPE.DIAMETER.DEFAULT / 2;
	private popover!: HTMLDivElement;
	input!: HTMLInputElement;
	css2dObject!: CSS2DObject;
	floor: Mesh | null = null;

	constructor(
		private renderer: ProjectRenderer,
		private manager: PlaygroundManager
	) {
		this.initPopover();
		this.addEventListeners();
	}

	get radius() {
		return this._radius;
	}

	set radius(value: number) {
		this._radius = value;
		this.setCircleDiameterInputValue();
		this.setCircleDiameterPopoverPosition();
		this.createFloor();
	}

	private createShape() {
		const vertices = this.manager.vertexList.toArray().reverse();

		const shape = new Shape();
		shape.autoClose = true;

		for (let index = 0; index < vertices.length; index++) {
			const vertex = vertices[index];

			if (!index) shape.moveTo(vertex.position.x, -vertex.position.y);
			else shape.lineTo(vertex.position.x, -vertex.position.y);
		}

		return shape;
	}

	private initPopover() {
		this.popover = document.createElement('div');
		this.popover.className = 'renderer-popover';

		this.input = document.createElement('input');
		this.input.className = 'renderer-input';
		this.input.inputMode = 'numeric';

		this.input.min = RENDERER_CONFIG.LINE_MIN_LENGTH.toString();
		this.input.max = RENDERER_CONFIG.LINE_MAX_LENGTH.toString();

		this.popover.appendChild(this.input);

		this.css2dObject = new CSS2DObject(this.popover);

		this.renderer.scene.add(this.css2dObject);

		this.css2dObject.visible = false;

		this.setCircleDiameterInputValue();
		this.setCircleDiameterPopoverPosition();
	}

	private addEventListeners() {
		this.input.addEventListener(
			'input',
			debounce(({ target }) => {
				const sanitized = target.value
					.replace(',', '.')
					.replace(/[^0-9.]/g, '');
				const value = Number(sanitized);

				const diameter = clamp(
					value,
					RENDERER_CONFIG.LINE_MIN_LENGTH,
					RENDERER_CONFIG.LINE_MAX_LENGTH
				);

				this.radius = diameter / 2;
			}, 1000)
		);
	}

	private setCircleDiameterInputValue() {
		this.input.value = `${Math.round(this.radius * 2 * 100) / 100} m`;
	}

	private setCircleDiameterPopoverPosition() {
		this.css2dObject.position.set(
			0,
			0.5,
			-this.radius - RENDERER_CONFIG.PLAYGROUND_LINE.LABEL_TO_LINE_OFFSET
		);
	}

	createFloor() {
		this.dispose();

		if (!this.manager.vertexList.isLoopClosed()) return;

		let surfaceArea = 0;

		switch (this.manager.shapeType) {
			case PlaygroundShapeType.RECTANGLE:
			case PlaygroundShapeType.CUSTOM: {
				const shape = this.createShape();
				const geometry = new ShapeGeometry(shape);
				geometry.rotateX(-(Math.PI / 2));

				const mat = this.customMaterial || DEFAULT_MATERIAL;

				this.floor = new Mesh(geometry, mat.clone());
				this.floor.position.set(0, RENDERER_CONFIG.FLOOR_Y, 0);

				surfaceArea = RenderUtils.computePolygonArea(
					this.manager.vertexList.toArray().map((v) => v.position)
				);
				break;
			}
			case PlaygroundShapeType.CIRCLE: {
				const geometry = new CircleGeometry(
					this.radius,
					RENDERER_CONFIG.CIRCLE_SHAPE.SEGMENTS
				);

				RenderUtils.generateCircleUVs(geometry, this.radius);

				const mat = this.customMaterial || DEFAULT_MATERIAL;

				this.floor = new Mesh(geometry, mat.clone());
				this.floor.position.set(0, RENDERER_CONFIG.FLOOR_Y, 0);
				this.floor.rotateX(-(Math.PI / 2));

				surfaceArea = Math.round(Math.PI * Math.pow(this.radius, 2));

				const frameShape = new Shape();
				frameShape.arc(0, 0, this.radius, 0, Math.PI * 2, false);

				frameShape.autoClose = true;

				const points = frameShape.getPoints(
					RENDERER_CONFIG.CIRCLE_SHAPE.SEGMENTS
				);

				const lineGeo = new LineGeometry();
				lineGeo.setFromPoints(points);

				const line = new Line2(lineGeo, lineMaterial);

				line.position.set(0, 0, LINE_POS_Y);
				this.floor.add(line);

				break;
			}

			default:
				break;
		}

		this.floor!.receiveShadow = true;
		this.renderer.project.add(this.floor!);
		this.css2dObject.visible =
			this.manager.shapeType === PlaygroundShapeType.CIRCLE;

		this.renderer.emitter.emit(ThreeEvent.CALCULATE_SURFACE_AREA, surfaceArea);
	}

	async setCustomMaterial(asset: Asset) {
		this.asset = asset;

		const textures = await Promise.all(
			Object.values(MapType).map((type) => {
				if (!asset.maps[type]) return null;

				const cached = this.assetsLib.texturesCache.get(asset.maps[type].path);
				if (cached) return cached;

				return this.loader
					.loadTexture(getFileSrc(asset.maps[type].path))
					.then((texture) => {
						this.assetsLib.texturesCache.set(asset.maps[type]!.path, texture);

						return texture;
					});
			})
		);

		RenderUtils.disposeMaterial(this.floor!);

		const cacheKey = `${asset.id}`;
		let material = this.assetsLib.materialCache.get(cacheKey);

		if (!material) {
			for (const texture of textures) {
				if (!texture) continue;

				texture.wrapS = RepeatWrapping;
				texture.wrapT = RepeatWrapping;
				texture.repeat.set(1, 1);
			}

			const [
				map,
				roughnessMap,
				normalMap,
				displacementMap,
				metalnessMap,
				aoMap,
				lightMap,
			] = textures;

			if (!map) throw new Error('MISSING_MAP');

			map.colorSpace = SRGBColorSpace;

			for (const texture of [
				roughnessMap,
				normalMap,
				displacementMap,
				metalnessMap,
				aoMap,
				lightMap,
			]) {
				if (!texture) continue;
				texture.colorSpace = LinearSRGBColorSpace;
			}

			material = new MeshStandardMaterial({
				map,
				normalScale: new Vector2(0.025, 0.025),
				displacementMap,
				displacementScale: 0.01,
				displacementBias: -0.01 / 2,
				metalnessMap,
				normalMap,
				roughnessMap,
				aoMap,
				lightMap,
				...asset.options,
			});

			this.assetsLib.materialCache.set(cacheKey, material);
		}

		this.customMaterial = material.clone();
		this.floor!.material = material;
	}

	dispose() {
		if (!this.floor) return;

		RenderUtils.disposeObject(this.floor);
	}

	export(): SerializedPlaygroundFloor {
		return {
			asset: this.asset,
			radius: this.radius,
		};
	}
}
